import { useCallback, useEffect, useState } from "react";
import { Link2, Loader2, Search, Unlink } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatDay } from "../../lib/finance";
import { SPEND_STATUS_LABELS, formatMoneyMap, marketingError } from "../../lib/marketing";
import { formatMinor } from "../../lib/finance";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Skeleton } from "../ui/skeleton";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

function LinkDialog({ campaignId, onClose, onDone }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      api.get("/marketing/expense-options", { params: { q } })
        .then(({ data }) => { if (alive) { setItems(data.items); setError(""); } })
        .catch((e) => { if (alive) { setItems([]); setError(marketingError(e, "Impossible de charger les dépenses.")); } });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  const link = async (expenseId) => {
    setBusy(expenseId);
    setError("");
    try {
      await api.post(`/marketing/campaigns/${campaignId}/spend`, { expense_id: expenseId });
      toast.success("Dépense reliée à la campagne");
      onDone();
    } catch (e) {
      setError(marketingError(e, "Impossible de relier cette dépense."));
    } finally {
      setBusy("");
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="spend-dialog">
        <DialogHeader>
          <DialogTitle>Relier une dépense</DialogTitle>
          <DialogDescription>Les dépenses que vous voyez dans les finances internes et qui ne sont reliées à aucune campagne. Une dépense appartient à une seule campagne.</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par titre…" className="pl-9 h-10" aria-label="Rechercher une dépense" data-testid="spend-search" />
        </div>
        {error && <p className="text-sm text-red-600" role="alert" data-testid="spend-error">{error}</p>}
        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 rounded-md border border-slate-200" data-testid="spend-options">
          {items === null && <div className="p-3"><Skeleton className="h-10 w-full" /></div>}
          {items?.length === 0 && !error && <p className="p-4 text-sm text-slate-500">Aucune dépense disponible.</p>}
          {items?.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-3 p-3" data-testid={`spend-option-${e.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{e.title}</div>
                <div className="text-xs text-slate-500">{formatMinor(e.amount_minor, e.currency)} · {e.incurred_at ? formatDay(e.incurred_at) : "sans date"} · {SPEND_STATUS_LABELS[e.status] || e.status}</div>
              </div>
              <Button size="sm" variant="outline" disabled={!!busy} onClick={() => link(e.id)} data-testid={`spend-link-${e.id}`}>
                {busy === e.id ? <Loader2 size={14} className="animate-spin" /> : "Relier"}
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={!!busy}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UnlinkDialog({ campaignId, link, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/marketing/campaigns/${campaignId}/spend/${link.id}/unlink`, { reason: reason.trim() });
      toast.success("Dépense détachée");
      onDone();
    } catch (e) {
      setError(marketingError(e, "Impossible de continuer. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid="unlink-dialog">
        <DialogHeader>
          <DialogTitle>Détacher « {link.expense?.title} » ?</DialogTitle>
          <DialogDescription>La dépense ne compte plus dans les coûts de la campagne. Elle reste dans les finances internes ; la ligne reste ici, barrée, avec son motif.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5"><Label htmlFor="unlink-reason">Motif</Label>
          <Textarea id="unlink-reason" rows={2} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="unlink-reason" /></div>
        {error && <p className="text-sm text-red-600" role="alert" data-testid="unlink-error">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Retour</Button>
          <Button variant="destructive" onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="unlink-submit">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Détacher</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// « Dépenses reliées » of a campaign: the references to Finance and their total per currency. Read with the cost right.
export default function CampaignSpend({ campaignId, canManage }) {
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [dialog, setDialog] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data: body } = await api.get(`/marketing/campaigns/${campaignId}/spend`);
      setData(body);
      setPhase("ready");
    } catch {
      setPhase("error");
    }
  }, [campaignId]);
  useEffect(() => { load(); }, [load]);
  const done = () => { setDialog(null); load(); };
  return (
    <section data-testid="campaign-spend">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-slate-900">Dépenses reliées</h3>
        {canManage && data?.can_link && <Button variant="outline" size="sm" onClick={() => setDialog("link")} data-testid="spend-add"><Link2 size={14} className="mr-1.5" />Relier une dépense</Button>}
      </div>
      {phase === "loading" && <Skeleton className="h-12 w-full" />}
      {phase === "error" && <p className="text-sm text-slate-600" data-testid="spend-load-error">Impossible de charger les dépenses. <Button variant="link" className="p-0 h-auto" onClick={load}>Réessayer</Button></p>}
      {phase === "ready" && (
        <>
          {!data.finance_active && <p className="text-sm text-slate-500" data-testid="spend-finance-off">Les finances internes ne sont pas activées : aucune dépense ne peut être reliée.</p>}
          {data.finance_active && data.items.length === 0 && <p className="text-sm text-slate-500" data-testid="spend-empty">Aucune dépense reliée. Sans elles, le coût par lead et le coût d'acquisition restent vides.</p>}
          {data.items.length > 0 && (
            <>
              <p className="text-sm text-slate-900 mb-2" data-testid="spend-total">Total : <span className="font-medium">{formatMoneyMap(data.totals) || "—"}</span></p>
              <ul className="divide-y divide-slate-100" data-testid="spend-list">
                {data.items.map((l) => (
                  <li key={l.id} className="flex items-start justify-between gap-3 py-2" data-testid={`spend-row-${l.id}`}>
                    <div className={`min-w-0 ${l.active ? "" : "text-slate-400"}`}>
                      <div className={`text-sm ${l.active ? "text-slate-900" : "line-through"}`}>{l.expense?.title || "Dépense indisponible"}</div>
                      <div className="text-xs text-slate-500">
                        {l.expense ? `${formatMinor(l.expense.amount_minor, l.expense.currency)} · ${l.expense.incurred_at ? formatDay(l.expense.incurred_at) : "sans date"}` : "Elle ne compte plus dans les finances"}
                        {l.linked_by ? ` · reliée par ${l.linked_by}` : ""}
                      </div>
                      {l.unlinked && <div className="text-xs text-slate-500">Détachée{l.unlinked.by ? ` par ${l.unlinked.by}` : ""} : {l.unlinked.reason}</div>}
                    </div>
                    {l.active && canManage && (
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-slate-600 shrink-0" aria-label={`Détacher ${l.expense?.title || "la dépense"}`} onClick={() => setDialog({ unlink: l })} data-testid={`spend-unlink-${l.id}`}><Unlink size={14} /></Button>
                    )}
                    {!l.active && <Badge variant="outline" className="text-2xs shrink-0">Détachée</Badge>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
      {dialog === "link" && <LinkDialog campaignId={campaignId} onClose={() => setDialog(null)} onDone={done} />}
      {dialog?.unlink && <UnlinkDialog campaignId={campaignId} link={dialog.unlink} onClose={() => setDialog(null)} onDone={done} />}
    </section>
  );
}
